import React from "react";

type ButtonProps = {
  color?: "primary" | "secondary" | "error" | "black";
  size?: "small" | "medium" | "large" | "fit";
  label: string;
  disable?: boolean;
  variant?: "standard" | "line" | "link" | "outline";
  onClick?: (val: any) => void;
  // onSubmit?: any;
  icon?: React.ReactNode | string;
  style?: React.CSSProperties;
  className?: string;
  loading?: boolean;
  type?: "button" | "submit" | "reset" | undefined;
};

export const Button = ({
  color = "primary",
  size = "fit",
  label,
  disable = false,
  variant = "standard",
  icon,
  style,
  className = "",
  loading = false,
  type = "button",
  ...props
}: ButtonProps) => {
  const sizeValues = {
    small: "h-7 min-w-[120px]",
    medium: "h-8 min-w-[144px]",
    large: "h-10 min-w-[144px]",
    fit: "p-0 px-5",
  };

  const colorValues = {
    primary:
      variant == "standard"
        ? "bg-light-primary-default text-white"
        : "text-light-primary-500 hover:text-[#00338D]",
    secondary:
      variant == "standard"
        ? "bg-white text-dark-primary-500"
        : "text-light-gray-300 hover:text-[#00338D]",
    error:
      variant == "standard"
        ? "bg-danger-50 text-danger-400"
        : "text-danger-500 hover:text-danger-600",
    black:
      variant == "standard"
        ? "bg-black text-white"
        : "text-white hover:bg-light-primary-default",
  };

  const variantValues = {
    standard: `justify-center p-2 px-3 shadow-[1px_1px_5px_rgba(0,0,0,0.14)] border border-transparent box-border rounded-md 
    ${
      disable || loading
        ? "bg-light-gray-25 text-light-gray-75"
        : colorValues[color] +
          `${
            color == "error"
              ? " hover:bg-danger-500 hover:text-white"
              : " hover:bg-light-primary-hover hover:text-white"
          }`
    } ${sizeValues[size]}`,
    line: `${
      disable || loading ? "text-dark-secondary-300" : colorValues[color]
    } h-7 min-w-max justify-start`,
    link: `bg-transparent border-b border-transparent hover:border-b-light-gray-200 py-2 transition-all`,
    outline: `justify-center p-2 px-3 border border-[#0A66C2] box-border rounded-md hover:border-light-primary-hover hover:shadow-[1px_1px_5px_rgba(0,0,0,0.14)] disabled:border-light-gray-300 disabled:cursor-not-allowed disabled:text-light-gray-300`,
  };

  return (
    <button
      type={type}
      className={`flex items-center text-sm transition-all duration-300 ${variantValues[variant]} ${
        sizeValues[size]
      } ${disable || loading ? "cursor-not-allowed" : "cursor-pointer"} ${
        icon ? "gap-1" : ""
      } ${className}`}
      disabled={loading ? true : disable}
      style={{ ...style }}
      {...props}
    >
      {loading ? (
        <div className="flex min-w-min justify-center gap-2">
          <div
            className={`h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 ${
              disable ? "border-white" : "border-gray-300"
            }`}
          ></div>
          loading...
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2">
          {icon && <span>{icon}</span>}
          {label}
        </div>
      )}
    </button>
  );
};
